import { useState, useCallback } from 'react';
import { auth } from '../firebase/firebaseConfig';
import { loadRazorpayScript, checkOrderStatus } from '../services/razorpayService';
import { createAddonOrder, verifyAddonPayment } from '../services/addonService';
import { customToast } from '../utils/toastUtils';

/**
 * useAddonPurchase
 *
 * Handles the complete add-on purchase flow:
 *  1. Save current resume state to sessionStorage
 *  2. Create Razorpay order via backend
 *  3. Open Razorpay inline checkout
 *  4. Verify payment on success
 *  5. Return success flag so caller can proceed with download
 *
 * Returns:
 *   { purchaseAddon, isPurchasing, purchaseSuccess }
 */
const useAddonPurchase = () => {
  const [isPurchasing, setIsPurchasing] = useState(false);
  const [purchaseSuccess, setPurchaseSuccess] = useState(false);

  /**
   * Save resume editor state to sessionStorage for restoration after payment
   */
  const saveResumeState = useCallback((stateObj) => {
    try {
      sessionStorage.setItem('addon_resume_state', JSON.stringify({
        ...stateObj,
        timestamp: Date.now()
      }));
    } catch (e) {
      console.warn('Could not save resume state to sessionStorage:', e);
    }
  }, []);

  /**
   * Restore saved resume state from sessionStorage
   */
  const restoreResumeState = useCallback(() => {
    try {
      const raw = sessionStorage.getItem('addon_resume_state');
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      // Expire after 30 minutes
      if (Date.now() - (parsed.timestamp || 0) > 30 * 60 * 1000) {
        sessionStorage.removeItem('addon_resume_state');
        return null;
      }
      return parsed;
    } catch (e) {
      return null;
    }
  }, []);

  /**
   * Clear saved resume state
   */
  const clearResumeState = useCallback(() => {
    sessionStorage.removeItem('addon_resume_state');
  }, []);

  /**
   * Execute the add-on purchase flow
   * @param {object} params
   * @param {object} params.addonData      - { id, actualPrice, currency, name }
   * @param {string} params.limitType      - 'download' | 'ats' | 'coverLetter'
   * @param {string} params.templateId     - current template ID
   * @param {string} params.templateTier   - 'silver' | 'gold' | 'platinum'
   * @param {object} [params.resumeState]  - optional state to save for post-payment restoration
   * @returns {Promise<boolean>} true if purchase succeeded
   */
  const purchaseAddon = useCallback(async ({ addonData, limitType, templateId, templateTier, resumeState }) => {
    const user = auth.currentUser;
    if (!user) {
      customToast.error('Please log in to make a purchase.');
      return false;
    }

    setIsPurchasing(true);
    setPurchaseSuccess(false);

    try {
      // 1. Save resume state if provided
      if (resumeState) {
        saveResumeState({
          ...resumeState,
          templateId,
          templateTier,
          action: limitType
        });
      }

      // 2. Load Razorpay script
      const scriptLoaded = await loadRazorpayScript();
      if (!scriptLoaded) {
        customToast.error('Unable to load payment gateway. Please check your connection and try again.');
        setIsPurchasing(false);
        return false;
      }

      // 3. Create order via backend
      const amountInPaise = Math.round(Number(addonData.actualPrice) * 100);
      const orderResult = await createAddonOrder({
        userId: user.uid,
        amount: amountInPaise,
        templateId: templateId || 'unknown',
        templateTier: templateTier || 'silver',
        limitType: limitType || 'download',
        addonId: addonData.id || 'singleDownloadAddon'
      });

      if (!orderResult.success) {
        customToast.error(orderResult.error || 'Failed to create payment order.');
        setIsPurchasing(false);
        return false;
      }

      // 4. Open Razorpay checkout
      const paymentResponse = await new Promise((resolve, reject) => {
        const options = {
          key: orderResult.keyId,
          amount: orderResult.amount,
          currency: orderResult.currency || 'INR',
          name: 'ResumeGlobe',
          description: addonData.name || 'Single Template Download',
          image: '/logo.png',
          order_id: orderResult.orderId,
          prefill: {
            name: user.displayName || '',
            email: user.email || ''
          },
          notes: {
            type: 'addon',
            limitType,
            templateId: templateId || '',
            templateTier: templateTier || ''
          },
          theme: {
            color: '#0f172a'
          },
          method: {
            upi: true,
            netbanking: true,
            card: true,
            wallet: true,
            paylater: true
          },
          handler: (response) => resolve(response),
          modal: {
            ondismiss: () => {
              const err = new Error('Payment cancelled');
              err.orderId = orderResult.orderId;
              reject(err);
            },
            escape: true,
            backdropclose: false
          }
        };

        const rzp = new window.Razorpay(options);
        rzp.on('payment.failed', (response) => {
          const err = new Error(response.error?.description || 'Payment failed');
          err.orderId = orderResult.orderId;
          reject(err);
        });
        rzp.open();
      });

      // 5. Verify payment
      const verifyResult = await verifyAddonPayment({
        razorpay_order_id: paymentResponse.razorpay_order_id,
        razorpay_payment_id: paymentResponse.razorpay_payment_id,
        razorpay_signature: paymentResponse.razorpay_signature,
        userId: user.uid,
        limitType,
        templateId: templateId || '',
        templateTier: templateTier || 'silver',
        addonId: addonData.id || 'singleDownloadAddon'
      });

      if (verifyResult.success) {
        setPurchaseSuccess(true);
        clearResumeState();
        customToast.success('Payment successful! Your download is ready.');
        setIsPurchasing(false);
        return true;
      } else {
        customToast.error('Payment verification failed. Please contact support.');
        setIsPurchasing(false);
        return false;
      }
    } catch (error) {
      // Before declaring failure, check server-side if money was actually captured
      if (error.orderId) {
        try {
          console.log('🔍 Addon: Checking order status server-side...', error.orderId);
          // Wait briefly for Razorpay to process (UPI delays)
          if (error.message !== 'Payment cancelled') {
            await new Promise(resolve => setTimeout(resolve, 3000));
          }
          const statusResult = await checkOrderStatus({
            orderId: error.orderId,
            userId: auth.currentUser?.uid,
            planType: null // addon, not a plan
          });
          if (statusResult.success && statusResult.status === 'paid') {
            console.log('✅ Addon payment was actually captured! Recovering...');
            setPurchaseSuccess(true);
            clearResumeState();
            customToast.success('Payment successful! Your download is ready.');
            setIsPurchasing(false);
            return true;
          }
        } catch (checkErr) {
          console.warn('⚠️ Addon order status check failed:', checkErr);
        }
      }

      if (error.message === 'Payment cancelled') {
        customToast.warning('Payment was cancelled.');
      } else {
        console.error('Addon purchase error:', error);
        customToast.error(error.message || 'Payment failed. Please try again.');
      }
      setIsPurchasing(false);
      return false;
    }
  }, [saveResumeState, clearResumeState]);

  return {
    purchaseAddon,
    isPurchasing,
    purchaseSuccess,
    saveResumeState,
    restoreResumeState,
    clearResumeState
  };
};

export default useAddonPurchase;
